import React, { useState, useEffect } from 'react';
import MobileSignedOutHeader from './MobileSignedOutHeader';
import MobileSignedInHeader from './MobileSignedInHeader';
import LinkButton from "~/ui-apps/design-system/atoms/LinkButton";
import { motion } from 'motion/react';
import { useLocation, useNavigate } from '@remix-run/react';

interface HeaderProps {
    setMobileMenuOpen: (open: boolean) => void;
    mobileMenuOpen: boolean;
    isAuthenticated: boolean;
}

const Header: React.FC<HeaderProps> = ({ setMobileMenuOpen, mobileMenuOpen, isAuthenticated = false }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState("hero");

    // Track active section based on scroll position
    useEffect(() => {
        const sections = ["hero", "features", "pricing", "faq"];
        const observer = new IntersectionObserver(
            (entries) => {
                // Find the section with the highest intersection ratio
                let maxRatio = 0;
                let maxEntry = null;

                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
                        maxRatio = entry.intersectionRatio;
                        maxEntry = entry;
                    }
                });

                if (maxEntry && maxRatio > 0.2) {
                    setActiveSection((maxEntry as IntersectionObserverEntry).target.id || "hero");
                }
            },
            {
                threshold: [0.2, 0.4, 0.6, 0.8, 1.0],
                rootMargin: "-15% 0px -15% 0px",
            }
        );

        sections.forEach((id) => {
            const element = document.getElementById(id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    // Smooth scroll handler
    const handleSmoothScroll = (
        e: React.MouseEvent<HTMLAnchorElement>,
        targetId: string,
    ) => {
        e.preventDefault();
        const element = document.querySelector(targetId);
        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    };

    const handleLogoNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (location.pathname === "/") {
            document.getElementById("hero")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            if (location.hash || location.search) {
                window.history.replaceState(null, "", "/");
            }
            return;
        }
        navigate("/", { state: { scrollToHero: true } });
    };

    return (
        <>

            {/* Desktop Public Header (Landing) */}
            {
                !isAuthenticated && (
                    <div id="desktop-header" className="hidden lg:block">
                        {/* Navigation */}
                        <motion.nav
                            className="fixed top-0 left-0 right-0 z-50 px-6 py-3"
                            style={{
                                background: "rgba(252, 252, 249, 0.8)",
                                backdropFilter: "blur(12px)",
                                WebkitBackdropFilter: "blur(12px)",
                            }}
                            initial={{ y: -100 }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <a href="/" onClick={handleLogoNavClick} className="text-inherit no-underline">
                                        <span className="tracking-tight text-lg font-bold flex items-center">
                                            MarketData
                                            <div className="bg-black rounded-md py-[2px] px-[5px] flex items-center justify-center ml-0.5">
                                                <span className="text-white text-sm">FYI</span>
                                            </div>
                                        </span>
                                    </a>
                                </div>
                                {location.pathname === "/" && (
                                    <div className="hidden md:flex items-center gap-8 text-sm">
                                        <a
                                            href="#features"
                                            onClick={(e) =>
                                                handleSmoothScroll(e, "#features")
                                            }
                                            className="relative hover:opacity-100 transition-opacity pb-1 font-medium"
                                        >
                                            Features
                                            {activeSection === "features" && (
                                                <motion.span
                                                    className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-black"
                                                    layoutId="activeNav"
                                                    transition={{
                                                        duration: 0.4,
                                                        ease: [0.25, 0.1, 0.25, 1],
                                                    }}
                                                />
                                            )}
                                        </a>
                                        <a
                                            href="#pricing"
                                            onClick={(e) => handleSmoothScroll(e, "#pricing")}
                                            className="relative hover:opacity-100 transition-opacity pb-1 font-medium"
                                        >
                                            Pricing
                                            {activeSection === "pricing" && (
                                                <motion.span
                                                    className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-black"
                                                    layoutId="activeNav"
                                                    transition={{
                                                        duration: 0.4,
                                                        ease: [0.25, 0.1, 0.25, 1],
                                                    }}
                                                />
                                            )}
                                        </a>
                                        <a
                                            href="#faq"
                                            onClick={(e) => handleSmoothScroll(e, "#faq")}
                                            className="relative hover:opacity-100 transition-opacity pb-1 font-medium"
                                        >
                                            FAQs
                                            {activeSection === "faq" && (
                                                <motion.span
                                                    className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-black"
                                                    layoutId="activeNav"
                                                    transition={{
                                                        duration: 0.4,
                                                        ease: [0.25, 0.1, 0.25, 1],
                                                    }}
                                                />
                                            )}
                                        </a>
                                        <a
                                            href="/about"
                                            className="relative hover:opacity-100 transition-opacity pb-1 font-medium"
                                        >
                                            About
                                        </a>
                                    </div>
                                )}
                                <div>
                                    <LinkButton type="outline" size="small" href="/sign-in">Sign In</LinkButton>
                                    <LinkButton type="primary" size="small" href="/sign-up" className="ml-5">Get Started</LinkButton>
                                </div>
                            </div>
                        </motion.nav>
                    </div>

                )
            }
        </>
    );
};

export default Header;
